import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { CoordinatesType } from "../view/View";
import { rotateVector3ToRef } from "./MathUtils";
export function getLocalMatrix(pNode) {
    const tMatrix = Matrix.Identity();
    getLocalMatrixToRef(pNode, tMatrix);
    return (tMatrix);
}
export function getLocalMatrixToRef(pNode, pResult) {
    if (pNode == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    Matrix.ComposeToRef(pNode.scaling, getRotationQuaternion(pNode), pNode.position, pResult);
}
export function setLocalMatrix(pNode, pMatrix) {
    if (pNode == null || pMatrix == null)
        return (false);
    if (pNode.rotationQuaternion == null)
        pNode.rotationQuaternion = new Quaternion();
    if (pMatrix.decompose(pNode.scaling, pNode.rotationQuaternion, pNode.position)) {
        return (true);
    }
    return (false);
}
/**
 * Returns the current rotation quaternion of pNode or creates a new one.
 * Note: Does not return a clone.
 */
export function getRotationQuaternion(pNode) {
    if (pNode == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    if (pNode.rotationQuaternion == null) {
        pNode.rotationQuaternion = Quaternion.RotationYawPitchRoll(pNode.rotation.y, pNode.rotation.x, pNode.rotation.z);
        pNode.rotation.copyFromFloats(0, 0, 0);
    }
    return (pNode.rotationQuaternion);
}
export function rotate(pNode, pRotation, pLocal, pRefPoint = null) {
    if (pNode == null || pRotation == null)
        return (false);
    const tNodeRotation = getRotationQuaternion(pNode);
    const tV1 = gTmpVector3_1;
    const tV2 = gTmpVector3_2;
    const tV3 = gTmpVector3_3;
    if (pLocal) {
        if (pRefPoint != null)
            rotateVector3ToRef(pRefPoint, tNodeRotation, tV1);
        //tNodeRotation * pRotation
        tNodeRotation.multiplyInPlace(pRotation);
        //correct the position
        if (pRefPoint != null) {
            rotateVector3ToRef(pRefPoint, getRotationQuaternion(pNode), tV2);
            tV1.subtractToRef(tV2, tV3);
            tV3.multiplyInPlace(pNode.scaling);
            pNode.position.addInPlace(tV3);
        }
    }
    else {
        //pRotation * tNodeRotation
        pRotation.multiplyToRef(tNodeRotation, tNodeRotation);
        //correct the position
        if (pRefPoint != null) {
            pNode.position.subtractToRef(pRefPoint, tV1);
            rotateVector3ToRef(tV1, pRotation, tV2);
            tV2.subtractToRef(tV1, tV3);
            pNode.position.addInPlace(tV2.subtract(tV1));
        }
    }
    return (true);
}
/**
 * Removes pNode and all its children from the scene.
 * Note: The parent of pMode is not changed or notified. pNode still counts as a children of its parent.
 */
export function removeFromScene(pNode, pScene) {
    if (pNode == null || pScene == null)
        return;
    if (pNode instanceof AbstractMesh)
        pScene.removeMesh(pNode);
    else
        pScene.removeTransformNode(pNode);
    //remove children
    const tChilds = pNode.getChildTransformNodes(true);
    for (const tChild of tChilds)
        removeFromScene(tChild, pScene);
}
/**
 * Adds pNode and all its children to the scene.
 * Note: No checks are made to ensure that the nodes are not already in the scene.
 */
export function addToScene(pNode, pScene) {
    if (pNode == null || pScene == null)
        return;
    if (pNode instanceof AbstractMesh)
        pScene.addMesh(pNode);
    else
        pScene.addTransformNode(pNode);
    //add children
    const tChilds = pNode.getChildTransformNodes(true);
    for (const tChild of tChilds)
        addToScene(tChild, pScene);
}
/**
     * Resizes a node in view space. The current scaling of the node will be overridden.
     * The node will be handled as if it is sphere. pWorldRadius is the assumed radius of the
     * sphere (e.g. radius of the bounding sphere). pPixelRadius is the desired radius in view space.
     */
export function resizeInViewSpace(pView, pNode, pWorldRadius, pPixelRadius) {
    if (pView == null || pNode == null)
        return;
    const tProj1 = pView.projectPoint2(pNode.position, CoordinatesType.Viewer, null, true);
    const tPos = gTmpVector3_1;
    pView.cameraControl.right.scaleToRef(pWorldRadius, tPos);
    pNode.position.addToRef(tPos, tPos);
    const tProj2 = pView.projectPoint2(tPos, CoordinatesType.Viewer, null, true);
    if (tProj1 != null && tProj2 != null) {
        const tV = tProj1.subtract(tProj2);
        const tScale = pPixelRadius / tV.length();
        pNode.scaling.x = tScale;
        pNode.scaling.y = tScale;
        pNode.scaling.z = tScale;
    }
}
export function updateFrozenWorldMatrix(pNode, pUpdateChilds = true) {
    if (pNode == null)
        return;
    if (pNode.isWorldMatrixFrozen)
        pNode.freezeWorldMatrix(); //calling freeze again will update the frozen world matrix
    if (pUpdateChilds) {
        const tChilds = pNode.getChildTransformNodes(true);
        for (const tChild of tChilds) {
            updateFrozenWorldMatrix(tChild, true);
        }
    }
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpVector3_1 = Vector3.Zero();
const gTmpVector3_2 = Vector3.Zero();
const gTmpVector3_3 = Vector3.Zero();
